import React from 'react';

import { Avatar } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/styles';

import * as Api from '../../../api/api';

import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';

const useStyles = makeStyles(() => ({
  wrapper: {
    border: '1px solid #d1d1d1', // matches the border color of our CKEditor
    borderBottom: 'none',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f8f8f8', // matches the background color of our CKEditor
    paddingTop: '3px',
    paddingBottom: '3px',
    paddingLeft: '5px',
  },
  avatar: {
    fontSize: '0.5rem',
  },
  chip: {
    // only display the fileName up to specified max width, then just append an ellipsis
    overFlow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
}));

type InputProps = {
  languageCode?: string;
  urlFileMappings?: Array<UrlFileMapping>;
  onDelete?: (urlFileMapping: UrlFileMapping) => () => void;
  isPreview?: boolean;
};

const MessageComposerFilesDisplay = ({
  availableFileMappings,
  classes,
  handleChipClick,
  onDelete,
  languageCode,
}: {
  availableFileMappings: UrlFileMapping[];
  classes: ClassNameMap;
  handleChipClick: (urlFileMapping: UrlFileMapping) => () => void;
  onDelete?: (urlFileMapping: UrlFileMapping) => () => void;
  languageCode?: string;
}) => (
  <div className={classes.wrapper}>
    <div className="u-margin-top-sm u-margin-left-xs u-alignSelf-flexStart">
      <MaterialDesignIcon iconName={ICON_NAMES.ATTACH_FILE} />
    </div>
    <div className="u-flexWrap-wrap">
      {availableFileMappings
        .sort((a, b) => a.urlKey.localeCompare(b.urlKey)) // sort based on key (i.e. url1, url2, etc.)
        .map((urlFileMapping) => (
          <Chip
            color="primary"
            variant="outlined"
            avatar={<Avatar className={classes.avatar}>{urlFileMapping.urlKey}</Avatar>}
            key={`url-key-${urlFileMapping.urlKey}`}
            className={`${classes.chip} u-margin-sm`}
            size="small"
            label={`${urlFileMapping.fileName}`}
            clickable
            onClick={handleChipClick(urlFileMapping)}
            onDelete={onDelete && onDelete(urlFileMapping)}
            data-cy={`toolbar-file-chip${languageCode ? `-${languageCode}` : ''}`}
          />
        ))}
    </div>
  </div>
);

const MessagePreviewFilesDisplay = ({
  availableFileMappings,
  classes,
  handleChipClick,
}: {
  availableFileMappings: UrlFileMapping[];
  classes: ClassNameMap;
  handleChipClick: (urlFileMapping: UrlFileMapping) => () => void;
}) => (
  <div className="u-flexWrap-wrap">
    {availableFileMappings
      .sort() // sort based on key (i.e. url1, url2, etc.)
      .map((urlFileMapping) => (
        <Chip
          color="primary"
          variant="outlined"
          avatar={<Avatar className={classes.avatar}>{urlFileMapping.urlKey}</Avatar>}
          key={`url-key-${urlFileMapping.urlKey}`}
          className={`${classes.chip} u-margin-sm`}
          size="small"
          label={`${urlFileMapping.fileName}`}
          clickable
          onClick={handleChipClick(urlFileMapping)}
          data-cy="preview-file-chip"
        />
      ))}
  </div>
);

export const FilesDisplay = (props: InputProps) => {
  const { languageCode, urlFileMappings, onDelete, isPreview } = props;
  const classes = useStyles();

  if (!urlFileMappings) {
    return null;
  }

  const availableFileMappings = urlFileMappings.filter((u) => !u.isDeleted);
  if (!availableFileMappings.length) {
    return null;
  }

  const handleChipClick = (urlFileMapping: UrlFileMapping) => async () => {
    const { downloadableUrl, organizationFileId } = urlFileMapping;
    // if there is no downloadableUrl, get one from server
    const downloadLink =
      downloadableUrl ||
      (await Api.get(`/files/${organizationFileId}/downloadableUrl`).then((r) => r.json()));
    window.open(downloadLink);
  };

  return isPreview ? (
    <MessagePreviewFilesDisplay
      availableFileMappings={availableFileMappings}
      handleChipClick={handleChipClick}
      classes={classes}
    />
  ) : (
    <MessageComposerFilesDisplay
      availableFileMappings={availableFileMappings}
      handleChipClick={handleChipClick}
      classes={classes}
      onDelete={onDelete}
      languageCode={languageCode}
    />
  );
};
